import { useCallback, useEffect, useState } from "react";
import { arrayMove } from "@dnd-kit/sortable";
import { nanoid } from "nanoid";
import {
  REFERENCE_MODE,
  REQUIRED_TYPES,
  RESUME_SECTION_TYPES,
  SECTION_STABLE_KEYS,
  SPACER_DEFAULT_SIZE,
} from "../components/b2bCandidate/cvOptimizer/optimizeResume/editor/config.js";
import {
  DUMMY_DATA,
  DUMMY_SECTIONS,
} from "../components/b2bCandidate/cvOptimizer/optimizeResume/data/dummyResume.js";
import { getResumeDataApi } from "../api/b2b-candidate/index.js";
import Utils from "../utils/Utils.js";
import WebUtils from "../utils/WebUtils.js";

const EMPTY_DATA_FOR_TYPE = {
  [RESUME_SECTION_TYPES.PERSONAL_INFO]: {
    fullName: "",
    headline: "",
    email: "",
    phone: "",
    addressLine: "",
    city: "",
    state: "",
    country: "",
    postalCode: "",
    dateOfBirth: "",
    nationality: "",
    workAuthorization: "",
    socialLinks: [],
  },
  [RESUME_SECTION_TYPES.SUMMARY]: "",
  [RESUME_SECTION_TYPES.EXPERIENCE]: [],
  [RESUME_SECTION_TYPES.EDUCATION]: [],
  [RESUME_SECTION_TYPES.SKILLS]: { technicalSkills: [], softSkills: [] },
  [RESUME_SECTION_TYPES.PROJECTS]: [],
  [RESUME_SECTION_TYPES.CERTIFICATIONS]: [],
  [RESUME_SECTION_TYPES.LANGUAGES]: [],
  [RESUME_SECTION_TYPES.AWARDS]: [],
  [RESUME_SECTION_TYPES.ACHIEVEMENTS]: [],
  [RESUME_SECTION_TYPES.PUBLICATIONS]: [],
  [RESUME_SECTION_TYPES.VOLUNTEER]: [],
  [RESUME_SECTION_TYPES.COURSEWORK]: [],
  [RESUME_SECTION_TYPES.INTERESTS]: [],
  [RESUME_SECTION_TYPES.REFERENCES]: {
    mode: REFERENCE_MODE.AVAILABLE_ON_REQUEST,
    items: [],
  },
  [RESUME_SECTION_TYPES.MEMBERSHIPS]: [],
  [RESUME_SECTION_TYPES.SPEAKING]: [],
  [RESUME_SECTION_TYPES.PATENTS]: [],
  [RESUME_SECTION_TYPES.SPACER]: { size: SPACER_DEFAULT_SIZE },
};

export default function useResumeStudio(resumeIdentifier, setLoading) {
  const userDetail = Utils.getUserDetail();
  // const [sections, setSections] = useState(DUMMY_SECTIONS);
  // const [sectionData, setSectionData] = useState(DUMMY_DATA);
  const [sections, setSections] = useState(DUMMY_SECTIONS);
  const [sectionData, setSectionData] = useState({});

  const [resumeMetaData, setResumeMetaData] = useState(null);
  const [allCollapsed, setAllCollapsed] = useState(false);

  const getResumeData = () => {
    const url = `${getResumeDataApi}?resumeIdentifier=${
      resumeIdentifier || null
    }&userIdentifier=${userDetail?.userIdentifier}`;
    setLoading(true);

    WebUtils.httpOperations(url, {}, "GET")
      .then((result) => {
        setLoading(false);
        if (
          result.data.resultStatusInfo.resultCode.toLowerCase() === "success"
        ) {
          const apiData = result?.data?.data?.resumeData ?? {};
          const apiOrder = result?.data?.data?.sectionOrder;

          const nextData = { ...apiData };
          if (Array.isArray(apiData.spacers)) {
            for (const sp of apiData.spacers) {
              if (sp?.id)
                nextData[sp.id] = { size: sp.size ?? SPACER_DEFAULT_SIZE };
            }
            delete nextData.spacers;
          }
          setSectionData(nextData);

          if (Array.isArray(apiOrder) && apiOrder.length) {
            setSections(
              apiOrder
                .filter((s) => s?.id && s?.type)
                .map((s) => ({ id: s.id, type: s.type })),
            );
          }

          setResumeMetaData({
            resumeFileName: result?.data?.data?.resumeFileName,
            resumeIdentifier: result?.data?.data?.resumeIdentifier,
            resumeOptimizerState: result?.data?.data?.resumeOptimizerState,
            uploadedDate: result?.data?.data?.uploadedDate,
          });
        } else {
        }
      })
      .catch((err) => {
        setLoading(false);
        console.log(err);
      });
  };

  useEffect(() => {
    if (resumeIdentifier) getResumeData();
  }, []);

  const updateSectionData = useCallback((id, data) => {
    setSectionData((prev) => ({ ...prev, [id]: data }));
  }, []);

  const addSection = useCallback((type) => {
    const isSpacer = type === RESUME_SECTION_TYPES.SPACER;
    const id = isSpacer
      ? `spacer-${nanoid(6)}`
      : (SECTION_STABLE_KEYS[type] ?? type);

    setSections((prev) => {
      if (!isSpacer && prev.some((s) => s.type === type)) return prev;
      return [...prev, { id, type }];
    });
    setSectionData((prev) => {
      if (!isSpacer && prev[id] !== undefined) return prev;
      return {
        ...prev,
        [id]: structuredClone(EMPTY_DATA_FOR_TYPE[type] ?? {}),
      };
    });
  }, []);

  const removeSection = useCallback((id) => {
    setSections((prev) => {
      const target = prev.find((s) => s.id === id);
      if (!target) return prev;
      if (REQUIRED_TYPES.includes(target.type)) return prev;
      return prev.filter((s) => s.id !== id);
    });
    setSectionData((prev) => {
      const next = { ...prev };
      delete next[id];
      return next;
    });
  }, []);

  const reorderSection = useCallback((activeId, overId) => {
    if (!overId || activeId === overId) return;
    setSections((prev) => {
      const oldIndex = prev.findIndex((s) => s.id === activeId);
      const newIndex = prev.findIndex((s) => s.id === overId);
      if (oldIndex < 0 || newIndex < 0) return prev;
      return arrayMove(prev, oldIndex, newIndex);
    });
  }, []);

  const applyParsedResume = useCallback((parsed) => {
    if (!parsed?.sections?.length) return;
    const nextSections = [];
    const nextData = {};
    const seenTypes = new Set();
    for (const s of parsed.sections) {
      const isSpacer = s.type === RESUME_SECTION_TYPES.SPACER;
      if (!isSpacer && seenTypes.has(s.type)) continue;
      seenTypes.add(s.type);
      const id = isSpacer
        ? `spacer-${nanoid(6)}`
        : (SECTION_STABLE_KEYS[s.type] ?? s.type);
      nextSections.push({ id, type: s.type });
      nextData[id] =
        s.data ?? structuredClone(EMPTY_DATA_FOR_TYPE[s.type] ?? {});
    }
    setSections(nextSections);
    setSectionData(nextData);
  }, []);

  const toggleCollapseAll = useCallback(() => {
    setAllCollapsed((v) => !v);
  }, []);

  const buildSavePayload = useCallback(() => {
    const resumeData = { ...sectionData };
    const spacers = [];

    for (const s of sections) {
      if (s.type === RESUME_SECTION_TYPES.SPACER) {
        spacers.push({
          id: s.id,
          size: sectionData?.[s.id]?.size ?? SPACER_DEFAULT_SIZE,
        });
        delete resumeData[s.id];
      }
    }
    resumeData.spacers = spacers;

    const sectionOrder = sections.map((s) => ({ id: s.id, type: s.type }));

    return { resumeData, sectionOrder };
  }, [sections, sectionData]);

  return {
    sections,
    sectionData,
    resumeMetaData,
    allCollapsed,
    updateSectionData,
    addSection,
    removeSection,
    reorderSection,
    applyParsedResume,
    toggleCollapseAll,
    buildSavePayload,
  };
}
